/**
 * The one description of a candidate's editable fields.
 *
 * Both drawers parse against this. Two schemas would be two chances to disagree with
 * the API, and the fields a person types are the same eighteen either way. The
 * difference between creating and editing is what the server does with them, not what
 * the form asks for.
 *
 * The screening fields are deliberately absent, exactly as they are from the edit
 * drawer. They move through the screening endpoints, which carry rules a plain write
 * walks straight past. A new candidate takes the values set in `toCreateBody` instead,
 * which is what makes "every candidate starts at Introduction" a fact in one line
 * rather than a default somebody can type over.
 *
 * Every check carries its own message. Zod's defaults are written for developers
 * ("Too big: expected number to be <=5"), and every one of these is rendered under an
 * input where somebody has to read it and know what to do.
 *
 * The schema also does the converting. A `<select>` hands back a string and the API
 * wants a number; an empty field means null to the server and "" to an input. Doing
 * that here means no component has to remember which fields are nullable, which is
 * exactly the mistake that used to reach the database as a 500.
 */

import { z } from 'zod';

import type { ApiError } from '@/api/client';
import type { CandidateCreate, CandidateRead, CandidateUpdate } from '@/api/schema';

import {
  APPLYING_FOR_LABEL,
  DOCUMENTATION_LABEL,
  PAYMENT_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
} from './labels';

/** What `<input type="date">` produces, and what the API accepts. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Deliberately loose, and client-side only: the API does not check email shape at all,
 * so this catches a typo rather than enforcing a rule.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * A union built from its own label record.
 *
 * The record is typed against the API's union, so a value the API adds is a compile
 * error in `labels.ts` before it can be a runtime surprise here.
 */
function enumOf<T extends string>(labels: Record<T, string>, message: string) {
  return z.enum(Object.keys(labels) as [T, ...T[]], message);
}

/** Optional free text: trimmed, and empty means null rather than "". */
function optionalText(max: number, tooLong: string) {
  return z
    .string()
    .trim()
    .max(max, tooLong)
    .transform((value) => (value === '' ? null : value));
}

/** A reference id from a dropdown. "" is the placeholder, and means null. */
const idOrNull = z
  .string()
  .trim()
  .transform((value) => (value === '' ? null : value));

/**
 * What a drawer holds in state.
 *
 * Everything a control produces is a string or a boolean, `sponsor_points` included,
 * because a `<select>` value is a string.
 */
export type CandidateFieldsDraft = {
  full_name: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  received_on: string;
  source: string;
  applying_for: string;
  department_id: string;
  preferred_position_id: string;
  preferred_country_id: string;
  sponsor_points: string;
  status: string;
  rsr_booking_date: string;
  documentation: string;
  payment_status: string;
  assigned_to: string;
  contacted: boolean;
  details_shared: boolean;
  followup_done: boolean;
};

export const candidateFieldsSchema = z.object({
  rsr_booking_date: z
    .string()
    .optional()
    .transform((value) => (value === '' ? null : (value ?? null))),
  full_name: z
    .string()
    .trim()
    .min(1, "Enter the candidate's name.")
    .max(200, 'Keep the name to 200 characters or fewer.'),

  // Optional, and only checked when something was typed. A WhatsApp lead often has a
  // number and nothing else, and the API accepts that.
  email: z
    .string()
    .trim()
    .max(320, 'Keep the email address to 320 characters or fewer.')
    .refine(
      (value) => value === '' || EMAIL_PATTERN.test(value),
      'Enter a valid email address.',
    )
    .transform((value) => (value === '' ? null : value)),

  phone: optionalText(40, 'Keep the phone number to 40 characters or fewer.'),
  city: z.string().trim().max(120, 'Keep the city to 120 characters or fewer.'),
  country: z.string().trim().max(120, 'Keep the country to 120 characters or fewer.'),

  received_on: z.string().regex(ISO_DATE, 'Choose the date this application arrived.'),
  source: enumOf(SOURCE_LABEL, 'Choose where this application came from.'),
  applying_for: enumOf(APPLYING_FOR_LABEL, 'Choose what they are applying for.'),

  department_id: idOrNull,
  preferred_position_id: idOrNull,
  preferred_country_id: idOrNull,

  sponsor_points: z.coerce
    .number({ error: 'Sponsor points run from 0 to 5.' })
    .multipleOf(0.5, 'Choose 1 to 5 in half-point steps.')
    .min(0, 'Sponsor points run from 0 to 5.')
    .max(5, 'Sponsor points run from 0 to 5.')
    .refine(
      (value) => value === 0 || value >= 1,
      'Choose Not rated, or 1 to 5 in half-point steps.',
    ),

  status: enumOf(STATUS_LABEL, 'Choose a status.'),
  documentation: enumOf(DOCUMENTATION_LABEL, 'Choose a documentation state.'),
  payment_status: enumOf(PAYMENT_LABEL, 'Choose a payment status.'),
  assigned_to: idOrNull,

  contacted: z.boolean(),
  details_shared: z.boolean(),
  followup_done: z.boolean(),
});

/** The parsed result: the API's own types, ready to send. */
export type CandidateFields = z.output<typeof candidateFieldsSchema>;

/** Documentation milestones and their existing flag describe the same decision. */
export function changeCandidateField<K extends keyof CandidateFieldsDraft>(
  draft: CandidateFieldsDraft,
  key: K,
  value: CandidateFieldsDraft[K],
): CandidateFieldsDraft {
  const next = { ...draft, [key]: value };
  if (key === 'status' && next.status === 'documentation_started')
    next.documentation = 'pending';
  if (key === 'status' && next.status === 'documentation_done') next.documentation = 'done';
  if (
    key === 'documentation' &&
    ['documentation_started', 'documentation_done'].includes(next.status)
  ) {
    next.status =
      next.documentation === 'done' ? 'documentation_done' : 'documentation_started';
  }
  return next;
}

/**
 * A blank form.
 *
 * Received defaults to today, which is right nine times in ten. Source and applying-for
 * are left empty on purpose: the API requires both and has no default for either, so
 * guessing would file a fabricated fact under a real person.
 */
export function emptyFields(today: string): CandidateFieldsDraft {
  return {
    full_name: '',
    email: '',
    phone: '',
    city: '',
    country: '',
    received_on: today,
    source: '',
    applying_for: '',
    department_id: '',
    preferred_position_id: '',
    preferred_country_id: '',
    sponsor_points: '0',
    status: 'candidate_applied',
    rsr_booking_date: '',
    documentation: 'pending',
    payment_status: 'not_applicable',
    assigned_to: '',
    contacted: false,
    details_shared: false,
    followup_done: false,
  };
}

/** The same fields read off an existing record, for the edit drawer. */
export function fieldsFrom(candidate: CandidateRead): CandidateFieldsDraft {
  return {
    full_name: candidate.full_name,
    email: candidate.email ?? '',
    phone: candidate.phone ?? '',
    city: candidate.city,
    country: candidate.country,
    received_on: candidate.received_on,
    source: candidate.source,
    applying_for: candidate.applying_for,
    department_id: candidate.department?.id ?? '',
    preferred_position_id: candidate.preferred_position?.id ?? '',
    preferred_country_id: candidate.preferred_country?.id ?? '',
    sponsor_points: String(candidate.sponsor_points),
    status: candidate.status,
    rsr_booking_date: candidate.rsr_scheduled_on ?? '',
    documentation: candidate.documentation,
    payment_status: candidate.payment_status,
    assigned_to: candidate.assigned_to ?? '',
    contacted: candidate.contacted,
    details_shared: candidate.details_shared,
    followup_done: candidate.followup_done,
  };
}

/**
 * The create body.
 *
 * The screening values are constants written here rather than fields on the form. That
 * is the whole of "a new candidate starts at Introduction": one line, in one place,
 * that nobody can type over.
 */
export function toCreateBody(fields: CandidateFields): CandidateCreate {
  return {
    ...fields,
    flyst_stage: 'introduction',
    rsr_state: 'pending',
    rsr_scheduled_on: null,
    rework_reasons: [],
  };
}

/** The update body. Every field, every time: the server applies exactly what it gets. */
export function toUpdateBody(fields: CandidateFields): CandidateUpdate {
  return { ...fields };
}

export type FieldMessages = Partial<Record<keyof CandidateFieldsDraft, string>>;

/**
 * One message per field, from a parse failure.
 *
 * The first only: a control has room for one line, and three complaints about the same
 * input are not more useful than the first one.
 */
export function messagesFrom(error: z.ZodError): FieldMessages {
  const messages: FieldMessages = {};
  for (const issue of error.issues) {
    const field = issue.path[0];
    if (typeof field === 'string' && !(field in messages)) {
      messages[field as keyof CandidateFieldsDraft] = issue.message;
    }
  }
  return messages;
}

/**
 * A server message for one field, from either shape the API uses.
 *
 * A validation failure lists its fields in `details.fields`. An unknown reference names
 * a single field in `details.field` instead (a different shape for a different error),
 * and without this branch it would show only as a banner with no indication of which
 * dropdown was wrong.
 */
export function serverMessageFor(
  error: ApiError | null,
  field: keyof CandidateFieldsDraft,
): string | undefined {
  if (!error) return undefined;
  if (error.code === 'UNKNOWN_REFERENCE' && error.details.field === field) return error.message;
  return error.fieldErrors.find((entry) => entry.field === field)?.message;
}
